import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import { Property } from 'src/services/properties/models'

type CalendarSyncCardProps = {
  property: Property
}

/**
 * Per-property Calendar Sync section. Shows the public iCal feed URL with a
 * copy-to-clipboard button and a Regenerate action that rotates the signed
 * token — which also invalidates any existing platform subscriptions.
 */
export const CalendarSyncCard = ({ property }: CalendarSyncCardProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [justCopied, setJustCopied] = useState(false)
  const [feedUrl, setFeedUrl] = useState(property.calendarFeedUrl)

  const regenerate = useMutation({
    mutationFn: () => propertyApi.csc.regenerateCalendarToken(property.id),
    onSuccess: (data) => {
      setFeedUrl(data.calendarFeedUrl)
      queryClient.invalidateQueries({ queryKey: propertyQueries.all() })
    },
  })

  const handleCopy = async () => {
    if (!feedUrl) return
    await navigator.clipboard.writeText(feedUrl)
    setJustCopied(true)
    setTimeout(() => setJustCopied(false), 2000)
  }

  const handleRegenerate = () => {
    const hasExistingUrl = Boolean(feedUrl)
    if (hasExistingUrl) {
      const confirmed = window.confirm(t('calendarSync.regenerateConfirm'))
      if (!confirmed) return
    }
    regenerate.mutate()
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-primary">{property.name}</h4>
      </div>

      {feedUrl ? (
        <>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={feedUrl}
              className="flex-1 font-mono text-xs"
              onFocus={(e) => e.target.select()}
            />
            <Button variant="ghost" onClick={handleCopy} className="shrink-0">
              {justCopied ? (
                <>
                  <CheckIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('calendarSync.copiedBtn')}
                </>
              ) : (
                <>
                  <ClipboardIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('calendarSync.copyBtn')}
                </>
              )}
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleRegenerate}
              disabled={regenerate.isPending}
              className="text-sm"
            >
              {regenerate.isPending
                ? t('calendarSync.regenerating')
                : t('calendarSync.regenerateUrl')}
            </Button>
            <p className="text-xs text-text-placeholder">{t('calendarSync.rotationWarning')}</p>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={handleRegenerate}
            disabled={regenerate.isPending}
            className="text-sm"
          >
            {regenerate.isPending
              ? t('calendarSync.generatingUrl')
              : t('calendarSync.generateFeedUrl')}
          </Button>
          <p className="text-xs text-text-placeholder">{t('calendarSync.feedDescription')}</p>
        </div>
      )}

      {regenerate.isError && (
        <p className="mt-2 text-sm text-error">{t('calendarSync.generateError')}</p>
      )}
    </div>
  )
}
